import * as Dialog from "@radix-ui/react-dialog"
import { Calendar, Copy, Download, FileText, X } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"

import { useClipboard } from "@/hooks/use-clipboard"
import { reportsPath } from "@/routes"

interface ReportData {
  markdown: string
  start_date: string
  end_date: string
  range: string
}

interface ExportReportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  range: string
}

function formatDateChip(startDate: string, endDate: string) {
  const start = new Date(startDate + "T00:00:00")
  const end = new Date(endDate + "T00:00:00")
  const startStr = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
  const endStr = end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
  const days =
    Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
  return { label: `${startStr} – ${endStr}`, days }
}

export function ExportReportModal({
  open,
  onOpenChange,
  range,
}: ExportReportModalProps) {
  const [report, setReport] = useState<ReportData | null>(null)
  // Derive loading state: we're loading when modal is open but the loaded
  // report doesn't match the current range yet. This avoids synchronous
  // setState calls in the useEffect body.
  const isLoading = open && report?.range !== range
  const [, copy] = useClipboard()

  useEffect(() => {
    if (!open) return

    let cancelled = false

    fetch(reportsPath({ range }), {
      headers: {
        Accept: "application/json",
        "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    })
      .then((r) => r.json() as Promise<ReportData>)
      .then((data) => {
        if (!cancelled) setReport(data)
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load report")
      })

    return () => {
      cancelled = true
    }
  }, [open, range])

  async function handleCopy() {
    if (!report) return
    const success = await copy(report.markdown)
    if (success) {
      toast.success("Copied to clipboard")
    }
  }

  function handleDownload() {
    if (!report) return
    const blob = new Blob([report.markdown], { type: "text/markdown" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `fig-report-${report.start_date}-to-${report.end_date}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const dateChip =
    report && !isLoading
      ? formatDateChip(report.start_date, report.end_date)
      : null

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-40"
          style={{ background: "rgba(42, 37, 56, 0.5)" }}
        />
        <Dialog.Content
          className="fixed inset-x-0 bottom-0 z-50 flex flex-col outline-none"
          style={{
            maxHeight: "85dvh",
            background: "var(--background)",
            borderRadius: "20px 20px 0 0",
            boxShadow:
              "0px 4px 16px rgba(91,84,120,0.18), 0px 16px 48px rgba(91,84,120,0.14)",
          }}
          aria-describedby={undefined}
        >
          {/* Drag handle + Header */}
          <div className="shrink-0 px-5 pt-3 pb-1">
            {/* Drag handle */}
            <div className="mb-3 flex justify-center">
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: "var(--border)",
                }}
              />
            </div>

            {/* Title row */}
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: "var(--secondary)",
                  }}
                >
                  <FileText
                    style={{
                      width: 16,
                      height: 16,
                      color: "var(--secondary-foreground)",
                    }}
                  />
                </div>
                <Dialog.Title
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Export Report
                </Dialog.Title>
              </div>

              <Dialog.Close
                className="flex items-center justify-center"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 15,
                  background: "var(--secondary)",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--muted-foreground)",
                }}
                aria-label="Close"
              >
                <X style={{ width: 16, height: 16 }} />
              </Dialog.Close>
            </div>

            {/* Date range chip */}
            {dateChip && (
              <div className="mt-2 mb-3 flex items-center gap-2">
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--muted-foreground)",
                  }}
                >
                  Showing
                </span>
                <span
                  className="flex items-center gap-1.5 rounded-lg px-2.5 py-1"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--secondary-foreground)",
                    background: "var(--secondary)",
                  }}
                >
                  <Calendar style={{ width: 12, height: 12 }} />
                  {dateChip.label}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--muted-foreground)",
                  }}
                >
                  · {dateChip.days} days
                </span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div
            style={{
              height: 1,
              background: "var(--border)",
              opacity: 0.5,
            }}
          />

          {/* Scrollable report preview */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {isLoading && (
              <div
                className="flex items-center justify-center py-12"
                style={{
                  color: "var(--muted-foreground)",
                  fontSize: 14,
                }}
              >
                Loading report…
              </div>
            )}
            {!isLoading && report && (
              <pre
                style={{
                  background: "var(--muted)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  padding: 16,
                  fontFamily: "'DM Mono', 'SF Mono', 'Fira Code', monospace",
                  fontSize: 12,
                  lineHeight: 1.65,
                  color: "var(--muted-foreground)",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  margin: 0,
                }}
              >
                {report.markdown}
              </pre>
            )}
          </div>

          {/* Divider */}
          <div
            style={{
              height: 1,
              background: "var(--border)",
              opacity: 0.5,
            }}
          />

          {/* Action buttons */}
          <div
            className="flex shrink-0 gap-3 px-5 py-4"
            style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
          >
            <button
              onClick={() => {
                void handleCopy()
              }}
              disabled={!report || isLoading}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3"
              style={{
                fontSize: 14,
                fontWeight: 600,
                background: "var(--primary)",
                color: "var(--primary-foreground)",
                border: "none",
                cursor: report && !isLoading ? "pointer" : "not-allowed",
                opacity: report && !isLoading ? 1 : 0.5,
              }}
            >
              <Copy style={{ width: 16, height: 16 }} />
              Copy to Clipboard
            </button>
            <button
              onClick={handleDownload}
              disabled={!report || isLoading}
              className="flex items-center justify-center gap-2 rounded-xl px-5 py-3"
              style={{
                fontSize: 14,
                fontWeight: 600,
                background: "var(--secondary)",
                color: "var(--secondary-foreground)",
                border: "none",
                cursor: report && !isLoading ? "pointer" : "not-allowed",
                opacity: report && !isLoading ? 1 : 0.5,
              }}
              aria-label="Download report"
            >
              <Download style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
